import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { SkuSupplier } from '../../types';

type EditableSupplier = Omit<SkuSupplier, 'id'> & { id?: string };

interface SkuSuppliersEditorProps {
  suppliers: EditableSupplier[];
  onChange: (suppliers: EditableSupplier[]) => void;
}

export default function SkuSuppliersEditor({ suppliers, onChange }: SkuSuppliersEditorProps) {
  const handleAdd = () => {
    onChange([
      ...suppliers,
      {
        organisationId: '',
        organisationName: '',
        supplierSku: '',
        price: '',
        leadTimeDays: undefined,
        moq: undefined,
        link: '',
      },
    ]);
  };

  const handleRemove = (index: number) => {
    onChange(suppliers.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: keyof EditableSupplier, value: string | number | undefined) => {
    const updated = [...suppliers];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <Label>Suppliers</Label>

      {suppliers.length > 0 && (
        <div className="space-y-3">
          {suppliers.map((supplier, index) => (
            <div key={index} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Supplier {index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => handleRemove(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Account Name</Label>
                  <Input
                    placeholder="Supplier name"
                    value={supplier.organisationName || ''}
                    onChange={(e) => handleChange(index, 'organisationName', e.target.value)}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Supplier SKU</Label>
                  <Input
                    placeholder="Supplier part #"
                    value={supplier.supplierSku || ''}
                    onChange={(e) => handleChange(index, 'supplierSku', e.target.value)}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={supplier.price || ''}
                    onChange={(e) => handleChange(index, 'price', e.target.value)}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Lead Time (days)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={supplier.leadTimeDays ?? ''}
                    onChange={(e) => handleChange(index, 'leadTimeDays', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">MOQ</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={supplier.moq ?? ''}
                    onChange={(e) => handleChange(index, 'moq', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Link</Label>
                  <Input
                    placeholder="https://..."
                    value={supplier.link || ''}
                    onChange={(e) => handleChange(index, 'link', e.target.value)}
                    className="h-8"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" size="sm" onClick={handleAdd} className="h-8">
        <Plus className="h-4 w-4 mr-1.5" />
        Add Supplier
      </Button>
    </div>
  );
}
